import { z } from "zod";

/**
 * Environment Variables Schema
 *
 * Validates all required environment variables at startup.
 * This prevents runtime errors from missing or invalid configuration.
 */
const envSchema = z.object({
  // Supabase Configuration (Required)
  VITE_SUPABASE_URL: z.string().url("Invalid Supabase URL"),
  VITE_SUPABASE_ANON_KEY: z.string().min(1, "Missing Supabase Anon Key"),

  // Firebase Configuration (Optional - for analytics and hosting)
  VITE_FIREBASE_API_KEY: z.string().optional(),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().optional(),
  VITE_FIREBASE_PROJECT_ID: z.string().optional(),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().optional(),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().optional(),
  VITE_FIREBASE_APP_ID: z.string().optional(),
  VITE_FIREBASE_MEASUREMENT_ID: z.string().optional(),
});

export type Env = z.infer<typeof envSchema>;

/**
 * Parse and validate environment variables
 * Throws an error if validation fails, preventing app from starting with invalid config
 */
const parseEnv = (): Env => {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    console.error("❌ Invalid environment variables:");
    console.error(result.error.flatten().fieldErrors);
    throw new Error(
      "Invalid environment configuration. Check console for details.",
    );
  }

  return result.data;
};

/**
 * Validated environment variables
 * Use this instead of import.meta.env for type safety
 */
export const env = parseEnv();
